import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, finalize } from 'rxjs/operators';
import Configs from '@shared/constants/configs';
import Utils from '@shared/utils/utils';
import { AuthService } from '@core/auth/auth.service';
import { SpinnerService } from '@shared/services/spinner.service';
import { Constants } from '@shared/constants/constants';
import { ApiService } from '@shared/apis';
import DateUtils from '@shared/utils/date-utils';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { TicketCreatedDetailComponent } from '@routes/dialogs/ticket-created-detail/ticket-created-detail.component';
import { TrainingVideoViewCountModalComponent } from '@routes/training-video/view-count-modal/training-video-view-count-modal.component';

export interface UserActivityData {
  login: { frequency: number; avgSessionDuration: string };
  created: { count: number; avgTat: string };
  canceled: { count: number; avgTat: string };
  viewed: { count: number; avgTat: string };
  edited: { count: number; avgTat: string };
  trainingEvents: { count: number; avgTat: string };
}

export type ActivityCardKey = any;

@Component({
  selector: 'app-user-activity-summary-section',
  templateUrl: './user-activity-summary-section.component.html',
  styleUrls: ['./user-activity-summary-section.component.scss'],
})
export class UserActivitySummarySectionComponent implements OnInit {
  @Input() isDealer: boolean = false;
  @Input() isHq: boolean = false;
  @Input() initialStartDate: Date | null = null;
  @Input() initialEndDate: Date | null = null;
  @Input() loading: boolean = false;
  @Input() error: string | null = null;
  @Input() data: UserActivityData | null = null;

  @Input() set userInfo(value) {
    const userArray = Array.isArray(value) ? value : [value];
    const currentUserIds = userArray.map((u) => u?._id).filter((id) => id);
    if (value.isDealer) {
      this.isDealer = true;
      this.isHQView = !!value.hqBpNo;
      this.userDetails = this.isHQView ? [value.hqBpNo] : value.bpList;
      this.hqBpNo = value.hqBpNo;
      this.bpList = value.bpList;
    } else {
      this.userDetails = userArray;
      // 부모에서 받은 user IDs가 변경되었는지 확인
      const previousUserIds = this.lastReceivedUserIds || [];
      const userIdsChanged =
        previousUserIds.length !== currentUserIds.length ||
        previousUserIds.some((id, index) => id !== currentUserIds[index]);
      this.lastReceivedUserIds = currentUserIds;

      // userInfo가 변경되고 user IDs가 변경되었을 때만 getUserActivitySummary 호출
      if (
        currentUserIds.length > 0 &&
        userIdsChanged &&
        this.startDate.value &&
        this.endDate.value
      ) {
        this.getUserActivitySummary();
      }
    }
  }

  @Output() applyDateRange = new EventEmitter<{
    start: Date | null;
    end: Date | null;
  }>();
  @Output() export = new EventEmitter<{
    start: Date | null;
    end: Date | null;
  }>();
  @Output() cardClick = new EventEmitter<ActivityCardKey>();
  @Output() detailUpdateEvent = new EventEmitter();

  readonly configs = Configs;
  readonly constants = Constants;
  /** 2025-10-05 이전 날짜는 선택 불가 (데이터 존재 시점) */
  readonly minSelectableDate: Date = DateUtils.getHqMidnight(
    new Date(2025, 9, 5),
  );

  /** End Date picker용 최소일: 시작일과 minSelectableDate 중 더 큰 값 */
  get minEndDate(): Date {
    const start = this.startDate.value;
    if (!start) return this.minSelectableDate;
    return start < this.minSelectableDate ? this.minSelectableDate : start;
  }

  startDate = new FormControl(null);
  endDate = new FormControl(null);
  startMaxDate: Date;
  today: Date;
  roles: string;
  userDetail: any; // 단일 user (하위 호환성 유지)
  userDetails: any[] = []; // user 배열
  company;
  activityData: any;
  ticketActivityData: any;
  videoActivityData: any;
  isProcessing = false;
  isHQView = false;
  hqBpNo: string;
  bpList: string[];
  private lastReceivedUserIds: string[] = [];

  bsConfig = {
    containerClass: 'theme-dk',
    showWeekNumbers: false,
    dateInputFormat: Configs.format.date,
  };

  readonly cardConfigs = [
    {
      key: 'login' as ActivityCardKey,
      title: 'Login Activity',
      icon: 'fa-sign-in-alt',
    },
    {
      key: 'created' as ActivityCardKey,
      title: 'Tickets Created',
      icon: 'fa-plus-circle',
    },
    {
      key: 'canceled' as ActivityCardKey,
      title: 'Tickets Canceled',
      icon: 'fa-times-circle',
    },
    {
      key: 'viewed' as ActivityCardKey,
      title: 'Tickets Viewed',
      icon: 'fa-eye',
    },
    {
      key: 'edited' as ActivityCardKey,
      title: 'Tickets Edited',
      icon: 'fa-edit',
    },
    {
      key: 'video' as ActivityCardKey,
      title: 'Training Video Viewed',
      icon: 'fa-edit',
    },
  ];

  bsModalRef: BsModalRef;

  constructor(
    private authService: AuthService,
    private apiService: ApiService,
    private modalService: BsModalService,
    private spinnerService: SpinnerService,
  ) {
    this.today = DateUtils.getHqMidnight(new Date());
    this.startMaxDate = this.today;
    this.company = this.authService.getCurrentCompany();
  }

  ngOnInit(): void {
    this.setDateRestriction();

    this.startDate.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => {
        if (
          this.startDate.value &&
          this.startDate.value < this.minSelectableDate
        ) {
          this.startDate.setValue(this.minSelectableDate);
          return;
        }
        this.getUserActivitySummary();
      });
    this.endDate.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((endDate) => {
        this.startMaxDate = endDate;
        if (this.startDate.value > endDate) {
          this.startDate.setValue(null);
        } else if (
          this.startDate.value &&
          this.startDate.value < this.minSelectableDate
        ) {
          this.startDate.setValue(this.minSelectableDate);
        }
        this.getUserActivitySummary();
      });

    this.getUserActivitySummary();
  }

  setDateRestriction(): void {
    // 기본값 설정: startDate는 한 달 전, endDate는 오늘 (minSelectableDate 이전은 선택 불가)
    const defaultEndDate =
      this.initialEndDate || DateUtils.getHqMidnight(new Date());
    const rawStartDate =
      this.initialStartDate || DateUtils.addDays(defaultEndDate, -7);
    const defaultStartDate =
      rawStartDate < this.minSelectableDate
        ? this.minSelectableDate
        : rawStartDate;

    this.today = defaultEndDate;
    this.startDate.setValue(defaultStartDate);
    this.endDate.setValue(defaultEndDate);
    this.startMaxDate = defaultEndDate;
  }

  toggleHQView(): void {
    this.isHQView = !this.isHQView;
    if (this.isHQView) {
      this.userDetails = [this.hqBpNo];
    } else {
      this.userDetails = this.bpList;
    }
    this.getUserActivitySummary();
  }

  getUserActivitySummary(): void {
    this.isProcessing = true;
    this.data = null;
    this.spinnerService.start();
    // 비딜러: userInfo가 단일 객체 { activityData, ticketLogs } 형태로 올 수 있음. userId는 userDetails/userDetail에서 추출
    const userPayload =
      this.userDetails && this.userDetails.length > 0
        ? this.userDetails[0]
        : this.userDetail;
    const userId = userPayload?._id;

    if (!this.startDate.value || !this.endDate.value) {
      this.isProcessing = false;
      this.spinnerService.stop();
      return;
    }

    if (this.isDealer) {
      this.apiService.user
        .getDealerActivitySummary({
          bpNo: this.isHQView ? this.hqBpNo : this.bpList,
          startDate: DateUtils.toHqMomentFormat(
            this.startDate.value,
            Configs.format.serverLocalDate,
          ),
          endDate: DateUtils.toHqMomentFormat(
            this.endDate.value,
            Configs.format.serverLocalDate,
          ),
        })
        .subscribe(
          (res) => {
            this.activityData = res.activityData;
            this.data = this.transformActivityData(res.activityData ?? []);
            this.ticketActivityData = res.ticketLogs;
            this.videoActivityData = res?.videoActivityData ?? [];
            this.isProcessing = false;
            this.spinnerService.stop();
          },
          (error) => {
            console.error('Error fetching dealer activity summary:', error);
            this.isProcessing = false;
            this.data = this.getEmptyActivityData();
            this.spinnerService.stop();
          },
        );
    } else {
      // 응답 형식: { activityData: [], ticketLogs: [] }
      this.apiService.user
        .getUserActivitySummary({
          userId,
          startDate: DateUtils.toHqMomentFormat(
            this.startDate.value,
            Configs.format.serverLocalDate,
          ),
          endDate: DateUtils.toHqMomentFormat(
            this.endDate.value,
            Configs.format.serverLocalDate,
          ),
        })
        .pipe(finalize(() => this.spinnerService.stop()))
        .subscribe(
          (res) => {
            const activityData = res?.activityData ?? [];
            const ticketLogs = res?.ticketLogs ?? [];
            this.videoActivityData = res?.videoActivityData ?? [];
            this.activityData = activityData;
            this.data = this.transformActivityData(res?.activityData ?? []);
            this.ticketActivityData = ticketLogs;
            this.isProcessing = false;
            this.spinnerService.stop();
          },
          (error) => {
            console.error('Error fetching user activity summary:', error);
            this.isProcessing = false;
            this.data = this.getEmptyActivityData();
            this.spinnerService.stop();
          },
        );
    }
  }

  private transformActivityData(rawData: any[]) {
    if (!rawData || !Array.isArray(rawData)) {
      return this.getEmptyActivityData();
    }

    // eventType별로 데이터 그룹화
    const grouped = rawData.reduce((acc, item) => {
      const eventType = item.eventType?.toLowerCase();
      if (!acc[eventType]) {
        acc[eventType] = [];
      }
      acc[eventType].push(item);
      return acc;
    }, {});

    // 각 이벤트 타입별 집계
    const loginEvents = grouped['login'] || [];
    const createdEvents = grouped['created'] || [];
    const canceledEvents = grouped['canceled'] || [];
    const viewedEvents = grouped['viewed'] || [];
    const editedEvents = grouped['edited'] || [];
    const trainingEvents = grouped['training'] || [];

    return {
      login: {
        frequency: this.sumCount(loginEvents),
        avgSessionDuration: this.calculateAvgSessionDuration(loginEvents),
      },
      created: {
        count: this.sumCount(createdEvents),
        avgTat: this.calculateAvgTat(createdEvents),
      },
      canceled: {
        count: this.sumCount(canceledEvents),
        avgTat: this.calculateAvgTat(canceledEvents),
      },
      viewed: {
        count: this.sumCount(viewedEvents),
        avgTat: this.calculateAvgTat(viewedEvents),
      },
      edited: {
        count: this.sumCount(editedEvents),
        avgTat: this.calculateAvgTat(editedEvents),
      },
      trainingEvents: {
        count: this.sumCount(trainingEvents),
        avgTat: this.calculateAvgTat(trainingEvents),
      },
    };
  }

  private sumCount(events: any[]): number {
    return events.reduce((sum, event) => sum + (event.count || 0), 0);
  }

  private calculateAvgSessionDuration(loginEvents: any[]): string {
    if (!loginEvents || loginEvents.length === 0) {
      return 'N/A';
    }

    // LOGIN 이벤트들 간의 시간 차이를 계산하여 평균 세션 시간 추정
    // 또는 auth.updatedAt - auth.createdAt을 사용할 수 있음
    const durations: number[] = [];

    loginEvents.forEach((event) => {
      if (event.auth?.createdAt && event.auth?.updatedAt) {
        const start = new Date(event.auth.createdAt).getTime();
        const end = new Date(event.auth.updatedAt).getTime();
        const duration = end - start;
        if (duration > 0) {
          durations.push(duration);
        }
      }
    });

    if (durations.length === 0) {
      return 'N/A';
    }

    const avgMs = durations.reduce((sum, d) => sum + d, 0) / durations.length;
    return this.formatDuration(avgMs);
  }

  private calculateAvgTat(events: any[]): string {
    if (!events || events.length === 0) {
      return 'N/A';
    }

    const tats: number[] = [];

    events.forEach((event) => {
      if (event.auth?.createdAt) {
        const start = new Date(event.auth.createdAt);
        const end = event.auth?.updatedAt
          ? new Date(event.auth.updatedAt)
          : new Date();
        const tat = Utils.tat(start, end);
        if (tat !== null && tat !== undefined) {
          tats.push(tat);
        }
      }
    });

    if (tats.length === 0) {
      return 'N/A';
    }

    const avgDays = tats.reduce((sum, t) => sum + t, 0) / tats.length;
    return this.formatTat(avgDays);
  }

  private formatDuration(ms: number): string {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days}d ${hours % 24}h`;
    } else if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    } else {
      return `${seconds}s`;
    }
  }

  private formatTat(days: number): string {
    const wholeDays = Math.floor(days);
    const hours = Math.floor((days - wholeDays) * 24);
    const minutes = Math.floor(((days - wholeDays) * 24 - hours) * 60);

    if (wholeDays > 0) {
      return hours > 0 ? `${wholeDays}d ${hours}h` : `${wholeDays}d`;
    } else if (hours > 0) {
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    } else {
      return '< 1m';
    }
  }

  private getEmptyActivityData(): UserActivityData {
    return {
      login: { frequency: 0, avgSessionDuration: 'N/A' },
      created: { count: 0, avgTat: 'N/A' },
      canceled: { count: 0, avgTat: 'N/A' },
      viewed: { count: 0, avgTat: 'N/A' },
      edited: { count: 0, avgTat: 'N/A' },
      trainingEvents: { count: 0, avgTat: 'N/A' },
    };
  }

  onStartDateChange(): void {
    // valueChanges 구독에서 처리하므로 여기서는 빈 메서드로 유지
    // 또는 필요시 추가 로직 구현
  }

  onEndDateChange(): void {
    // valueChanges 구독에서 처리하므로 여기서는 빈 메서드로 유지
    // 또는 필요시 추가 로직 구현
  }

  private triggerSearch(): void {
    // start date만 입력되거나, end date만 입력되거나, 둘 다 입력될 때 검색
    if (this.startDate.value || this.endDate.value) {
      this.applyDateRange.emit({
        start: this.startDate.value,
        end: this.endDate.value,
      });
      this.isHQView = true;
      this.getUserActivitySummary();
    }
  }

  onExport(): void {
    if (!this.data) {
      return;
    }

    // 엑셀 데이터 생성
    const excelData = this.convertDataToExcelFormat();
    const csvContent = this.convertToCSV(excelData);
    this.downloadExcel(csvContent);
  }

  private convertDataToExcelFormat(): any[][] {
    const rows: any[][] = [];

    // 헤더 행
    rows.push(['[Login Activity]', '', '']);
    rows.push(['Activity Type', 'Metric', 'Value']);

    // Login Activity
    if (this.data.login) {
      rows.push([
        'Login Activity',
        'Frequency',
        this.data.login.frequency || 0,
      ]);
    }

    // Tickets Created (ticketActivityData 우선 사용)
    rows.push(['Tickets Created', 'Count', this.getTicketCreatedCount()]);

    // CREATED 이벤트의 상세 항목 추가
    const createdDetails = this.calculateCreatedEventDetails();
    if (createdDetails.totalCreatedTicket > 0) {
      rows.push([]); // 빈 행 추가
      rows.push(['[Tickets Created Details]', '', '']);
      rows.push([
        'Total Created Ticket',
        '',
        createdDetails.totalCreatedTicket,
      ]);

      // Type of Tickets
      if (createdDetails.typeOfTicketEntries.length > 0) {
        rows.push([]);
        rows.push(['[Type of Tickets]', '', '']);
        createdDetails.typeOfTicketEntries.forEach((item) => {
          rows.push([item.key, '', item.value]);
        });
      }

      // Product Category
      if (createdDetails.productCategoryEntries.length > 0) {
        rows.push([]);
        rows.push(['[Product Category]', '', '']);
        createdDetails.productCategoryEntries.forEach((item) => {
          rows.push([item.key, '', item.value]);
        });
      }

      // Resolution (기타 항목)
      if (createdDetails.otherEntries.length > 0) {
        rows.push([]);
        rows.push(['[Resolution]', '', '']);
        createdDetails.otherEntries.forEach((item) => {
          rows.push([item.key, '', item.value]);
        });
      }
    }

    // Tickets Canceled
    if (this.data.canceled) {
      rows.push(['Tickets Canceled', 'Count', this.data.canceled.count || 0]);
    }

    // Tickets Viewed
    if (this.data.viewed) {
      rows.push(['Tickets Viewed', 'Count', this.data.viewed.count || 0]);
    }

    // Tickets Edited
    if (this.data.edited) {
      rows.push(['Tickets Edited', 'Count', this.data.edited.count || 0]);
    }

    // --- Video Activity Data (raw) ---
    if (
      this.videoActivityData &&
      Array.isArray(this.videoActivityData) &&
      this.videoActivityData.length > 0
    ) {
      rows.push([]);
      rows.push(['[Video Activity]', '', '']);
      rows.push(['Video Title', 'Count']);
      this.videoActivityData.forEach((item: any) => {
        rows.push([item.videoTitle ?? '', item.count ?? '']);
      });
    }

    return rows;
  }

  private calculateCreatedEventDetails(): {
    totalCreatedTicket: number;
    typeOfTicketItems: { [key: string]: number };
    productCategoryItems: { [key: string]: number };
    otherItems: { [key: string]: number };
    typeOfTicketEntries: Array<{ key: string; value: number }>;
    productCategoryEntries: Array<{ key: string; value: number }>;
    otherEntries: Array<{ key: string; value: number }>;
  } {
    // 초기화
    let totalCreatedTicket = 0;
    const typeOfTicketItems: { [key: string]: number } = {};
    const productCategoryItems: { [key: string]: number } = {};
    const otherItems: { [key: string]: number } = {};

    // ticketActivityData 형식: { result, serviceType, productType, category } 집계
    if (this.ticketActivityData && Array.isArray(this.ticketActivityData)) {
      this.ticketActivityData.forEach((item: any) => {
        const count = item.result ?? 0;
        totalCreatedTicket += count;

        // serviceType → typeOfTicketEntries
        const serviceType = item.serviceType ?? item._id?.serviceType;
        if (serviceType) {
          const key = String(serviceType).toUpperCase();
          typeOfTicketItems[key] = (typeOfTicketItems[key] ?? 0) + count;
        }

        // productType → productCategoryEntries
        const productType = item.productType ?? item._id?.productType;
        if (productType) {
          const key = String(productType).toUpperCase();
          productCategoryItems[key] = (productCategoryItems[key] ?? 0) + count;
        }

        // category → otherEntries (Resolution 등)
        const category = item.category ?? item._id?.status;
        if (category) {
          const key = String(category).toUpperCase();
          otherItems[key] = (otherItems[key] ?? 0) + count;
        }
      });
    }

    // 0이 아닌 값만 필터링하여 반환
    const typeOfTicketEntries = Object.keys(typeOfTicketItems)
      .filter((key) => typeOfTicketItems[key] > 0)
      .map((key) => ({ key, value: typeOfTicketItems[key] }))
      .sort((a, b) => a.key.localeCompare(b.key));

    const productCategoryEntries = Object.keys(productCategoryItems)
      .filter((key) => productCategoryItems[key] > 0)
      .map((key) => ({ key, value: productCategoryItems[key] }))
      .sort((a, b) => a.key.localeCompare(b.key));

    const otherEntries = Object.keys(otherItems)
      .filter((key) => otherItems[key] > 0)
      .map((key) => ({ key, value: otherItems[key] }))
      .sort((a, b) => a.key.localeCompare(b.key));

    return {
      totalCreatedTicket,
      typeOfTicketItems,
      productCategoryItems,
      otherItems,
      typeOfTicketEntries,
      productCategoryEntries,
      otherEntries,
    };
  }

  private convertToCSV(data: any[][]): string {
    return data
      .map((row) => {
        return row
          .map((cell) => {
            // 셀 값에 쉼표나 따옴표가 있으면 따옴표로 감싸고 내부 따옴표는 이스케이프
            const cellValue = cell != null ? String(cell) : '';
            if (
              cellValue.includes(',') ||
              cellValue.includes('"') ||
              cellValue.includes('\n')
            ) {
              return `"${cellValue.replace(/"/g, '""')}"`;
            }
            return cellValue;
          })
          .join(',');
      })
      .join('\n');
  }

  private downloadExcel(csvContent: string): void {
    // BOM 추가 (UTF-8 엑셀 호환성)
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], {
      type: 'text/csv;charset=utf-8;',
    });

    // 파일명 생성
    const fileName = `USER-ACTIVITY-${DateUtils.toHqMomentFormat(
      new Date(),
      'YYYYMMDDHHmmssSSS',
    )}.csv`;

    // 다운로드 링크 생성
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // 메모리 해제
    URL.revokeObjectURL(url);
  }

  onCardClick(key: ActivityCardKey): void {
    if (key === 'created') {
      this.openCreatedTicketsModal();
    } else if (key === 'training') {
      const userId: any = this.isDealer
        ? !this.isHQView
          ? this.bpList
          : this.hqBpNo
        : this.lastReceivedUserIds[0];
      this.modalService.show(TrainingVideoViewCountModalComponent, {
        class: 'modal-md',
        initialState: {
          userId,
          startDate: DateUtils.toHqMomentFormat(
            this.startDate.value,
            Configs.format.serverLocalDate,
          ),
          endDate: DateUtils.toHqMomentFormat(
            this.endDate.value,
            Configs.format.serverLocalDate,
          ),
          isDealer: this.isDealer,
        },
      });
    } else {
      this.cardClick.emit(key);
    }
  }

  private openCreatedTicketsModal(): void {
    if (!this.ticketActivityData || !Array.isArray(this.ticketActivityData)) {
      return;
    }

    const config: any = {
      initialState: {
        createdEvents: this.ticketActivityData,
      },
      class: 'modal-lg',
      ignoreBackdropClick: true,
    };

    this.bsModalRef = this.modalService.show(
      TicketCreatedDetailComponent,
      config,
    );
  }

  getTicketCreatedCount(): number {
    if (this.ticketActivityData && Array.isArray(this.ticketActivityData)) {
      return this.ticketActivityData.reduce(
        (sum: number, item: { result?: number }) => sum + (item.result ?? 0),
        0,
      );
    }
    return this.data?.created?.count ?? 0;
  }

  getVideoViewCount(): number {
    if (this.videoActivityData && Array.isArray(this.videoActivityData)) {
      return this.videoActivityData.length;
    }
    return 0;
  }

  getCardData(key: ActivityCardKey) {
    if (!this.data) {
      return null;
    }
    return this.data[key];
  }

  getCardTitle(key: ActivityCardKey): string {
    const config = this.cardConfigs.find((c) => c.key === key);
    return config?.title || '';
  }
}
